/**
 * useDateBuckets — group thread summaries into Today / Yesterday /
 * This Week / Earlier buckets.
 *
 * Implemented as a pure function (`bucketThreads`) plus a thin hook
 * that memoises against the threads array.  The pure function is
 * what we unit-test; the hook just memoises so a re-render of the
 * sheet doesn't recompute on every key press.
 *
 * Pattern matches the date segregation ChatGPT mobile and Claude
 * mobile both use (per the #261 spec).  Bucket boundaries are
 * computed against `now` (injectable for tests; defaults to
 * `Date.now()` at call time).
 *
 * "This Week" = within the last 7 days (excluding today + yesterday).
 * "Earlier"  = anything older than 7 days.
 *
 * Pinned threads (PR2 #261) will get a dedicated "Pinned" bucket at
 * the top.  For PR1 they sort normally inside their date bucket.
 */

import { useMemo } from 'react';
import type { ThreadSummary } from '../api/threadsApi';

export type DateBucketKey = 'pinned' | 'today' | 'yesterday' | 'thisWeek' | 'earlier';

export interface DateBucket {
  key: DateBucketKey;
  /** Display label, e.g. "Today". */
  label: string;
  threads: ThreadSummary[];
}

const BUCKET_ORDER: DateBucketKey[] = [
  'pinned',
  'today',
  'yesterday',
  'thisWeek',
  'earlier',
];

const LABELS: Record<DateBucketKey, string> = {
  pinned: 'Pinned',
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  earlier: 'Earlier',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Returns the local midnight (00:00) for a given epoch ms. */
function localMidnight(epoch: number): number {
  const d = new Date(epoch);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function classifyDate(
  ts: string | number | Date,
  nowEpoch: number,
): DateBucketKey {
  const t = typeof ts === 'string' ? Date.parse(ts) : +new Date(ts);
  if (Number.isNaN(t)) return 'earlier';
  const todayMidnight = localMidnight(nowEpoch);
  const yesterdayMidnight = todayMidnight - MS_PER_DAY;
  const weekMidnight = todayMidnight - 7 * MS_PER_DAY;
  if (t >= todayMidnight) return 'today';
  if (t >= yesterdayMidnight) return 'yesterday';
  if (t >= weekMidnight) return 'thisWeek';
  return 'earlier';
}

/** Pure: bucket threads by pin state first, then `last_message_at`.
 *  Pinned threads float to a dedicated "Pinned" bucket at the top
 *  (per the #261 spec); the rest fall through the date buckets.
 *  Empty buckets are omitted so the renderer doesn't show empty
 *  section headers. */
export function bucketThreads(
  threads: ThreadSummary[],
  now: number = Date.now(),
): DateBucket[] {
  const map: Record<DateBucketKey, ThreadSummary[]> = {
    pinned: [],
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  };
  for (const t of threads) {
    if (t.pinned) {
      map.pinned.push(t);
      continue;
    }
    map[classifyDate(t.last_message_at, now)].push(t);
  }
  return BUCKET_ORDER
    .filter((k) => map[k].length > 0)
    .map((k) => ({ key: k, label: LABELS[k], threads: map[k] }));
}

export function useDateBuckets(threads: ThreadSummary[]): DateBucket[] {
  // Re-bucket on threads change.  We deliberately don't depend on
  // a wall-clock tick — a thread that crosses midnight while the
  // sheet is open will only re-bucket on the next list re-render,
  // which is fine for v1.
  return useMemo(() => bucketThreads(threads), [threads]);
}
